import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateStrings } from "../../../../../app/common";
import { useAppSelector } from "../../../../../app/hooks";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import { ReactComponent as ClockIcon } from "../../../../../common/icons/clock.svg";
import { ReactComponent as EditIcon } from "../../../../../common/icons/editGray.svg";
import { EmploymentsHierarchy } from "../../../../talents/talentAPI";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import AccumulationsModal from "../../Accumulations/AccumulationsModal";
import { ContinuousEmploymentRangeDialog } from "./ContinuousEmploymentRangeDialog";

interface Props {
  open: boolean;
  setOpen: (val: boolean) => void;
  title: string;
  inner?: boolean;
  bundleId?: string;
  employmentBundle?: EmploymentsHierarchy;
  onRangeUpdated?: () => void;
}

const BundleHeader = ({
  open,
  setOpen,
  title,
  inner,
  bundleId,
  employmentBundle,
  onRangeUpdated,
}: Props) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<boolean>(false);
  const [editingRange, setEditingRange] = useState<boolean>(false);
  // TODO: render component only after data is loaded
  // localContEmptId to ensure that the component is re-rendered when the continuousEmploymentId loaded
  const [localContEmpId, setLocalContEmpId] = useState<string | null>(null);

  const { canEditEmployment, isAdmin } = useContext(AccessRightContext);
  const canEdit = Boolean(
    employmentBundle?.employerId &&
      canEditEmployment(employmentBundle?.employerId),
  );

  useEffect(() => {
    if (employmentBundle?.continuousEmploymentId) {
      setLocalContEmpId(employmentBundle.continuousEmploymentId);
    }
  }, [employmentBundle]);

  const translatedEmploymentTypes = useAppSelector(
    (state) => state.localized.employmentType,
  );

  const formattedDate = employmentBundle?.startDate
    ? formatDateStrings(employmentBundle.startDate, employmentBundle.endDate)
    : "";

  const closeEditingModal = () => {
    setEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        backgroundColor: inner ? "#F1EDF7" : "#FFFFFF",
        alignItems: "center",
        padding: "5px 0px",
        paddingLeft: inner ? "14px" : "0px",
        borderBottom: "1px solid #E5E5E5",
      }}
    >
      <IconButton
        size="small"
        onClick={() => setOpen(!open)}
        sx={{ padding: 0, margin: 0, marginLeft: 1, color: "#4A1D9C" }}
      >
        {!open ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </IconButton>
      {employmentBundle?.isShift && (
        <Box sx={{ display: "flex", marginLeft: "10px", flexShrink: 0 }}>
          <ClockIcon aria-hidden="true" />
        </Box>
      )}
      <Box
        sx={{
          whiteSpace: "nowrap",
          color: inner ? "#000000" : "#4A1D9C",
          fontWeight: 700,
          marginLeft: inner ? "10px" : "22px",
          marginRight: "4px",
          fontSize: "14px",
        }}
      >
        <Typography variant="header">{title.toLocaleUpperCase()}</Typography>
      </Box>
      {formattedDate && bundleId && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box sx={{ marginLeft: "10px" }}>
            <Typography color="text.secondary" variant="body2">
              {formattedDate}
            </Typography>
          </Box>
          {canEdit && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
            {isAdmin && employmentBundle?.continuousEmploymentId && (
                <Tooltip title={t("talentProfile:continuousEmploymentRange")}>
                  <IconButton
                    size="small"
                    onClick={() => setEditingRange(true)}
                  >
                    <CalendarMonthIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => setEditing(true)}>
                <EditIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
      {inner && employmentBundle?.employmentType && (
        <Box
          sx={{
            fontSize: "12px",
            marginLeft: "auto",
            marginRight: "20px",
            color: "rgba(0, 0, 0, 0.52)",
          }}
        >
          {translatedEmploymentTypes[employmentBundle.employmentType]}
        </Box>
      )}
      {editingRange &&
        employmentBundle?.continuousEmploymentId &&
        employmentBundle.startDate && (
          <ContinuousEmploymentRangeDialog
            open={editingRange}
            continuousEmploymentId={employmentBundle.continuousEmploymentId}
            startDate={employmentBundle.startDate}
            endDate={employmentBundle.endDate}
            onClose={() => setEditingRange(false)}
            onUpdated={() => onRangeUpdated?.()}
          />
        )}
      {editing && bundleId && (
        <DialogComponent open={editing} onClose={closeEditingModal}>
          <AccumulationsModal
            closeEditingModal={closeEditingModal}
            dateRange={[
              employmentBundle?.startDate || null,
              employmentBundle?.endDate || null,
            ]}
            continuousEmploymentId={localContEmpId as string}
          />
        </DialogComponent>
      )}

    </Box>
  );
};

export default BundleHeader;
